import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

// Assets
import HeroImage from '../assets/banner.png';
import ScienceFairImage from '../assets/science_fair.png';
import SportsDayImage from '../assets/sports_day.png';
import PrincipalPhoto from '../assets/samalai_maharana.jpeg';
import Logo from '../assets/logo.png';
import EntranceImage from '../assets/entrance.png';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    toast.success('Message sent! We will get back to you soon.');
    setFormData({ name: '', email: '', phone: '', message: '' });
    setIsSubmitting(false);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen font-sans text-gray-900 bg-amber-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-amber-50/90 backdrop-blur-md shadow-sm border-b border-amber-100">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src={Logo} alt="Sishu Public School Logo" className="w-12 h-12 object-contain" />
            <div className="flex flex-col">
              <span className="text-lg font-bold tracking-tight text-amber-900 leading-none">Sishu Public School</span>
              <span className="text-[10px] font-bold text-orange-600 tracking-[0.2em] uppercase">Berhampur</span>
            </div>
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-semibold text-gray-600 uppercase tracking-widest">
            <button onClick={() => scrollToSection('home')} className="hover:text-orange-600 transition-colors">Home</button>
            <button onClick={() => scrollToSection('about')} className="hover:text-orange-600 transition-colors">About</button>
            <button onClick={() => scrollToSection('news')} className="hover:text-orange-600 transition-colors">News</button>
            <button onClick={() => scrollToSection('gallery')} className="hover:text-orange-600 transition-colors">Gallery</button>
            <button onClick={() => scrollToSection('contact')} className="hover:text-orange-600 transition-colors px-6 py-2 bg-orange-600 text-white rounded-full">Contact</button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative h-screen flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img src={HeroImage} alt="School" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-amber-900/40 backdrop-brightness-50"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10 text-white">
          <div className="max-w-3xl animate-in fade-in slide-in-from-bottom-8 duration-700">
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight">
              Empowering Minds, <br />
              <span className="text-amber-400">Shaping Futures.</span>
            </h1>
            <p className="text-xl md:text-2xl mb-10 text-gray-200 leading-relaxed font-light">
              Welcome to Sishu Public School. We provide a nurturing environment where every child is inspired to learn, grow, and excel.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
              <button
                onClick={() => scrollToSection('about')}
                className="px-8 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-full font-bold shadow-lg shadow-orange-900/20 transition-all hover:scale-105"
              >
                Learn Our Story
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white border border-white/30 rounded-full font-bold transition-all hover:scale-105"
              >
                Enrollment Info
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-amber-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="lg:w-1/2 relative">
              <div className="absolute -top-6 -left-6 w-32 h-32 bg-amber-200 rounded-full -z-10 animate-pulse"></div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-amber-100 rounded-full -z-10"></div>
              <img src={PrincipalPhoto} alt="Principal" className="rounded-3xl shadow-2xl relative z-10 w-full object-cover aspect-[4/5]" />
              <div className="absolute bottom-10 -right-10 bg-white p-6 rounded-2xl shadow-xl z-20 hidden md:block max-w-xs">
                <p className="text-orange-700 font-bold mb-1">Mrs. Samalai Maharana</p>
                <p className="text-gray-500 text-sm italic">"Our mission is to nurture creativity and leadership in every student."</p>
              </div>
            </div>
            <div className="lg:w-1/2">
              <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">About Our School</span>
              <h2 className="text-4xl font-bold mb-8 text-amber-950">A Strong Foundation for Your Child Since 1995</h2>
              <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                Founded with a vision to provide quality education to young learners in our community, Sishu Public School has grown into a beloved institution known for its nurturing environment and holistic approach to development.
              </p>
              <div className="grid grid-cols-2 gap-8 mt-12">
                <div className="space-y-2">
                  <h4 className="text-3xl font-bold text-orange-600">25+</h4>
                  <p className="text-gray-500 font-medium">Years of Experience</p>
                </div>
                <div className="space-y-2">
                  <h4 className="text-3xl font-bold text-orange-600">1000+</h4>
                  <p className="text-gray-500 font-medium">Primary Graduates</p>
                </div>
                <div className="space-y-2">
                  <h4 className="text-3xl font-bold text-orange-600">30+</h4>
                  <p className="text-gray-500 font-medium">Caring Teachers</p>
                </div>
                <div className="space-y-2">
                  <h4 className="text-3xl font-bold text-orange-600">15:1</h4>
                  <p className="text-gray-500 font-medium">Student-Teacher Ratio</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* News Section */}
      <section id="news" className="py-24">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-end mb-16">
            <div>
              <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">Happenings</span>
              <h2 className="text-4xl font-bold text-amber-950">Latest School News</h2>
            </div>
            <button className="text-orange-600 font-bold hover:underline mb-2">View News Archive →</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-3xl mb-6 shadow-lg shadow-gray-200">
                <img src={ScienceFairImage} alt="Science Fair" className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full text-xs font-bold text-amber-600 uppercase tracking-widest">Academic</div>
              </div>
              <h3 className="text-2xl font-bold mb-3 group-hover:text-orange-600 transition-colors">Annual Junior Science Fair 2024</h3>
              <p className="text-gray-500 font-medium mb-4">May 15, 2024 • School Campus</p>
              <p className="text-gray-600 leading-relaxed max-w-lg">
                Our students displayed incredible creativity at this year's science fair, featuring hand-crafted models of sustainable gardens and solar energy projects.
              </p>
            </div>

            <div className="group cursor-pointer">
              <div className="relative overflow-hidden rounded-3xl mb-6 shadow-lg shadow-gray-200">
                <img src={SportsDayImage} alt="Sports Day" className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full text-xs font-bold text-orange-600 uppercase tracking-widest">Athletics</div>
              </div>
              <h3 className="text-2xl font-bold mb-3 group-hover:text-orange-600 transition-colors">Inter-School Sports Meet Participants</h3>
              <p className="text-gray-500 font-medium mb-4">April 28, 2024 • Indoor Stadium</p>
              <p className="text-gray-600 leading-relaxed max-w-lg">
                Our primary athletic teams have qualified for the local finals after a spectacular performance in last week's school clusters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Campus Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
            <div className="lg:w-1/2">
              <img src={EntranceImage} alt="School Entrance" className="rounded-3xl shadow-2xl w-full object-cover" />
            </div>
            <div className="lg:w-1/2">
              <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">Our Campus</span>
              <h2 className="text-4xl font-bold mb-8 text-amber-950">A Welcoming Environment for Every Child</h2>
              <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                Our school entrance stands as a gateway to knowledge and innovation. Located in the heart of Berhampur, our campus is designed to be safe, vibrant, and conducive to learning.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                From our colorful murals to our spacious classrooms, every corner of Sishu Public School is built with the student in mind.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats/CTA Mini Section */}
      <section className="bg-amber-800 py-16 text-white text-center">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold mb-8">Join the Sishu Public School Family Today</h2>
          <button
            onClick={() => scrollToSection('contact')}
            className="px-10 py-4 bg-amber-500 hover:bg-amber-400 text-white rounded-full font-bold shadow-2xl shadow-black/20 transition-all hover:-translate-y-1"
          >
            Schedule a Campus Tour
          </button>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">Visual Journey</span>
            <h2 className="text-4xl font-bold text-amber-950">Campus Gallery</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="space-y-4">
              <img src="https://images.unsplash.com/photo-1546410531-bb4caa6b424d?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Junior Library" />
              <img src="https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Primary Classroom" />
            </div>
            <div className="space-y-4 pt-12">
              <img src="https://images.unsplash.com/photo-1577896851231-70ef18881754?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Building" />
              <img src="https://images.unsplash.com/photo-1544391682-17efd932e008?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Field" />
            </div>
            <div className="space-y-4">
              <img src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Study" />
              <img src="https://images.unsplash.com/photo-1516534775068-ba3e84529519?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Kids' Computer Lab" />
            </div>
            <div className="space-y-4 pt-8">
              <img src="https://images.unsplash.com/photo-1524388676246-339241b7113e?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Music Room" />
              <img src="https://images.unsplash.com/photo-1588072432836-e10032774350?auto=format&fit=crop&w=800&q=80" className="rounded-2xl grayscale hover:grayscale-0 transition-all cursor-pointer" alt="Art Room" />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-amber-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
            <div>
              <span className="text-orange-600 font-bold tracking-widest uppercase text-sm mb-4 block">Get In Touch</span>
              <h2 className="text-4xl font-bold text-amber-950 mb-8">Contact Our Admissions Office</h2>
              <p className="text-gray-600 mb-10 text-lg">
                Have questions? We're here to help. Reach out to us for more information about the admission process, school tours, or academic programs.
              </p>

              <div className="space-y-8">
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center text-orange-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-900 uppercase tracking-widest">Call Us</p>
                    <p className="text-xl font-medium text-gray-700">+91 9437XXXXXX</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center text-orange-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-900 uppercase tracking-widest">Email Us</p>
                    <p className="text-xl font-medium text-gray-700">info@spsbam.in</p>
                  </div>
                </div>
                <div className="flex items-center space-x-6">
                  <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center text-orange-600">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-amber-900 uppercase tracking-widest">Visit Us</p>
                    <p className="text-xl font-medium text-gray-700">Brahmapur, Odisha, India</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-10 rounded-3xl shadow-2xl shadow-amber-900/5">
              <h3 className="text-2xl font-bold mb-8 text-amber-950">Send a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-amber-50 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 transition-all outline-none"
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-amber-50 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 transition-all outline-none"
                    placeholder="john@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase mb-2">Your Message</label>
                  <textarea
                    rows="4"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-6 py-4 bg-amber-50 border-transparent rounded-2xl focus:bg-white focus:border-amber-500 transition-all outline-none resize-none"
                    placeholder="How can we help you?"
                    required
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-orange-600 hover:bg-orange-700 text-white rounded-full font-bold shadow-xl shadow-orange-900/10 transition-all"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message Now'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-amber-950 pt-20 pb-10 text-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-8">
                <img src={Logo} alt="Sishu Public School Logo" className="w-12 h-12 object-contain" />
                <span className="text-2xl font-bold tracking-tight">Sishu Public School</span>
              </div>
              <p className="text-gray-400 max-w-sm leading-relaxed mb-8">
                Excellence in education, character, and leadership since 1995. Nurturing the next generation of global citizens.
              </p>
              <div className="flex space-x-6">
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors">
                  <span className="sr-only">Facebook</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" /></svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-colors">
                  <span className="sr-only">Twitter</span>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" /></svg>
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-8 uppercase tracking-widest text-sm text-amber-400">Quick Links</h4>
              <ul className="space-y-4 text-gray-400 font-medium">
                <li><button onClick={() => scrollToSection('home')} className="hover:text-white transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('news')} className="hover:text-white transition-colors">Latest News</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-white transition-colors">Admission</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-8 uppercase tracking-widest text-sm text-amber-400">Contact Info</h4>
              <ul className="space-y-4 text-gray-400 font-medium">
                <li>Brahmapur, Odisha, India</li>
                <li>+91 9437XXXXXX</li>
                <li>info@spsbam.in</li>
              </ul>
            </div>
          </div>
          <div className="pt-10 border-t border-white/5 text-center text-gray-500 text-sm">
            <p>© 2024 Sishu Public School. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;